import { useCallback, useEffect, useState } from "react";
import {
  Download,
  Eye,
  Pencil,
  Search,
  Star,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import axiosInstance from "../api/axiosInstance";

const PAGE_SIZE = 20;
const api = {
  list: (params) =>
    axiosInstance.get("/api/superadmin/testimonials/", { params }),
  get: (id) => axiosInstance.get(`/api/superadmin/testimonials/${id}/`),
  patch: (id, body) =>
    axiosInstance.patch(`/api/superadmin/testimonials/${id}/`, body),
  remove: (id) => axiosInstance.delete(`/api/superadmin/testimonials/${id}/`),
  formalize: (review) =>
    axiosInstance.post(
      "/api/superadmin/testimonial-formalization/",
      { review },
      { timeout: 90000 },
    ),
  designations: () =>
    axiosInstance.get("/api/superadmin/master/designations/", {
      params: { page_size: 100 },
    }),
};
const reviewerName = (user) => user?.name || user?.email || "—";
const formatDate = (date) =>
  date
    ? new Date(date).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    })
    : "—";
const testimonialStatus = (item) =>
  !item.is_approved ? "Pending" : item.is_active ? "Published" : "Unpublished";

function Stars({ rating, showValue = true, size = 17 }) {
  return (
    <span className="tm-stars" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((value) => (
        <Star
          key={value}
          size={size}
          fill={value <= rating ? "currentColor" : "none"}
        />
      ))}
      {showValue && <b>{rating}/5</b>}
    </span>
  );
}
function StatusBadge({ item }) {
  const status = testimonialStatus(item);
  return (
    <span className={`tm-badge ${status.toLowerCase()}`}>
      <i />
      {status}
    </span>
  );
}
function IconButton({ label, tone = "", children, ...props }) {
  return (
    <button
      type="button"
      className={`tm-icon ${tone}`}
      title={label}
      aria-label={label}
      {...props}
    >
      {children}
    </button>
  );
}
function Modal({ title, children, onClose, closeOnBackdrop = true, disableClose = false }) {
  return (
    <div
      className="tm-modal-backdrop"
      onClick={closeOnBackdrop ? onClose : undefined}
    >
      <section
        className="tm-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <h2>{title}</h2>
            <p>Customer feedback management</p>
          </div>
          <IconButton label="Close dialog" onClick={onClose} disabled={disableClose}>
            <X size={18} />
          </IconButton>
        </header>
        <div className="tm-modal-content">{children}</div>
      </section>
    </div>
  );
}

const formInput = {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid #d7deeb",
  borderRadius: 9,
  font: "inherit",
  boxSizing: "border-box",
  color: "#172b4d",
};
function TestimonialForm({
  testimonial,
  designations,
  onSave,
  onFormalize,
  saving,
  formalizing,
  onFormalizingChange,
}) {
  const [form, setForm] = useState({
    rating: testimonial.rating,
    review: testimonial.review,
    reviewer_type: testimonial.reviewer_type,
    contact_person_name: testimonial.contact_person_name || "",
    contact_person_designation_ids: (testimonial.contact_person_designations || []).map(
      (designation) => designation.id,
    ),
  });
  const [error, setError] = useState("");
  const [comparison, setComparison] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState("");
  const [hasFormalizedReview, setHasFormalizedReview] = useState(false);
  const [designationSearch, setDesignationSearch] = useState("");
  const matchingDesignations = designations.filter((designation) =>
    designation.name.toLowerCase().includes(designationSearch.trim().toLowerCase()),
  );
  const submit = () => {
    const rating = Number(form.rating);
    if (!Number.isInteger(rating) || rating < 1 || rating > 5)
      return setError("Rating must be a whole number from 1 to 5.");
    if (!form.review.trim()) return setError("Review text is required.");
    onSave({
      ...form,
      rating,
      review: form.review.trim(),
      contact_person_name: form.contact_person_name.trim(),
    });
  };
  const formalize = async () => {
    if (!form.review.trim()) {
      setError("Enter review text before formalizing it.");
      return;
    }
    setError("");
    onFormalizingChange(true);
    try {
      const formalizedReview = await onFormalize(form.review.trim());
      if (!formalizedReview) throw new Error("No formalized content was returned.");
      setComparison({
        original: form.review.trim(),
        formalized: formalizedReview,
      });
      setSelectedVersion("");
    } catch (requestError) {
      setError(
        requestError.response?.data?.detail ||
        "Unable to formalize the review right now.",
      );
    } finally {
      onFormalizingChange(false);
    }
  };

  return (
    <>
      <div className="tm-edit-grid">
        <label className="tm-field">
          Business name
          <input style={formInput} value={testimonial.business_name || "—"} disabled />
        </label>
        <label className="tm-field">
          Rating
          <input
            style={formInput}
            type="number"
            min="1"
            max="5"
            value={form.rating}
            onChange={(e) => setForm({ ...form, rating: e.target.value })}
          />
        </label>
        <label className="tm-field">
          Reviewer type
          <select
            style={formInput}
            value={form.reviewer_type}
            onChange={(e) => setForm({ ...form, reviewer_type: e.target.value })}
          >
            <option value="buyer">Buyer</option>
            <option value="supplier">Supplier</option>
            <option value="default">Default</option>
          </select>
        </label>
        <label className="tm-field">
          Contact person name
          <input
            style={formInput}
            value={form.contact_person_name}
            onChange={(e) => setForm({ ...form, contact_person_name: e.target.value })}
            placeholder="Enter contact person name"
          />
        </label>
        <label className="tm-field">
          Contact person designations
          <input
            style={formInput}
            value={designationSearch}
            onChange={(e) => setDesignationSearch(e.target.value)}
            placeholder="Search designations..."
          />
          <select
            style={{ ...formInput, minHeight: 110 }}
            multiple
            value={form.contact_person_designation_ids.map(String)}
            onChange={(e) => {
              const visibleIds = new Set(matchingDesignations.map((designation) => designation.id));
              const hiddenSelections = form.contact_person_designation_ids.filter(
                (id) => !visibleIds.has(id),
              );
              const selectedVisible = Array.from(
                e.target.selectedOptions,
                (option) => Number(option.value),
              );
              setForm({
                ...form,
                contact_person_designation_ids: [...hiddenSelections, ...selectedVisible],
              });
            }}
          >
            {matchingDesignations.map((designation) => (
              <option value={designation.id} key={designation.id}>
                {designation.name}
              </option>
            ))}
          </select>
          <small className="tm-help">Hold Ctrl/Cmd to select more than one.</small>
        </label>
        <div className="tm-formalize-panel">
          <span>Review assistant</span>
          <p>Improve grammar and clarity while preserving the customer’s meaning.</p>
          <button
            type="button"
            className="tm-primary"
            onClick={formalize}
            disabled={formalizing || saving || hasFormalizedReview}
            title={hasFormalizedReview ? "Edit the review text before formalizing it again." : undefined}
          >
            {formalizing ? "Formalizing…" : "Formalize content"}
          </button>
        </div>
      </div>
      {selectedVersion && (
        <div className={`tm-selection-notice ${selectedVersion}`}>
          {selectedVersion === "formalized"
            ? "Formalized content selected. Edit the review manually to enable formalization again."
            : "Original content selected."}
        </div>
      )}
      <label className="tm-field tm-edit-full">
        Review
        <textarea
          className={`tm-review-input${hasFormalizedReview ? " formalized" : ""}`}
          style={{
            ...formInput,
            minHeight: 150,
            resize: "vertical",
            lineHeight: 1.6,
          }}
          value={form.review}
          onChange={(e) => {
            setForm({ ...form, review: e.target.value });
            setHasFormalizedReview(false);
            setSelectedVersion("");
          }}
        />
      </label>
      {error && <p className="tm-error">{error}</p>}
      <div className="tm-modal-actions">
        <button
          type="button"
          className="tm-primary"
          onClick={submit}
          disabled={saving || formalizing}
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
      {comparison && (
        <Modal
          title="Compare review content"
          closeOnBackdrop={false}
          onClose={() => setComparison(null)}
        >
          <p className="tm-comparison-intro">
            Choose the version to keep in the review editor.
          </p>
          <div className="tm-comparison-grid">
            <section className="tm-comparison-card">
              <span>Original content</span>
              <p>{comparison.original}</p>
              <button
                type="button"
                className="tm-secondary"
                onClick={() => {
                  setForm((current) => ({ ...current, review: comparison.original }));
                  setHasFormalizedReview(false);
                  setSelectedVersion("original");
                  setComparison(null);
                }}
              >
                Keep original
              </button>
            </section>
            <section className="tm-comparison-card formalized">
              <span>Formalized content</span>
              <p>{comparison.formalized}</p>
              <button
                type="button"
                className="tm-primary"
                onClick={() => {
                  setForm((current) => ({ ...current, review: comparison.formalized }));
                  setHasFormalizedReview(true);
                  setSelectedVersion("formalized");
                  setComparison(null);
                }}
              >
                Use formalized
              </button>
            </section>
          </div>
        </Modal>
      )}
    </>
  );
}
function Detail({ item, onEdit, onPublishToggle, onDeactivate, saving }) {
  const fields = [
    ["Reviewer", reviewerName(item.created_by)],
    ["Reviewer email", item.created_by?.email || "—"],
    ["Business name", item.business_name || "—"],
    ["Contact person", item.contact_person_name || "—"],
    [
      "Designations",
      item.contact_person_designations?.map((designation) => designation.name).join(", ") || "—",
    ],
    ["Rating", <Stars rating={item.rating} />],
    ["Reviewer type", item.reviewer_type],
    ["Status", <StatusBadge item={item} />],
    ["Approved by", reviewerName(item.approved_by)],
    ["Created", formatDate(item.created_at)],
    ["Updated", formatDate(item.updated_at)],
  ];
  return (
    <div className="tm-detail">
      <div className="tm-detail-grid">
        {fields.map(([label, value]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>
      {item.company_logo_url && (
        <div className="tm-detail-logo">
          <span>Company logo</span>
          <img
            src={item.company_logo_url}
            alt="Company logo"
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
          />
        </div>
      )}
      <article>
        <span>Full review</span>
        <p>{item.review}</p>
      </article>
      <div className="tm-detail-actions">
        <button type="button" className="tm-secondary" onClick={onEdit} disabled={saving}>
          <Pencil size={14} /> Edit
        </button>
        <button
          type="button"
          className={item.is_active ? "tm-secondary tm-unpublish-action" : "tm-primary"}
          onClick={onPublishToggle}
          disabled={saving}
        >
          {item.is_active ? <Download size={14} /> : <Upload size={14} />}
          {item.is_active ? "Unpublish" : "Publish"}
        </button>
        <button type="button" className="tm-danger" onClick={onDeactivate} disabled={saving}>
          <Trash2 size={14} /> Deactivate
        </button>
      </div>
    </div>
  );
}

export default function Testimonials() {
  const [items, setItems] = useState([]),
    [count, setCount] = useState(0),
    [page, setPage] = useState(1),
    [loading, setLoading] = useState(true),
    [message, setMessage] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    reviewer_type: "",
    status: "",
    rating: "",
  }),
    [selected, setSelected] = useState(null),
    [mode, setMode] = useState(null),
    [action, setAction] = useState(null),
    [saving, setSaving] = useState(false),
    [formalizing, setFormalizing] = useState(false),
    [designations, setDesignations] = useState([]);
  const [, setSchemaData] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.list({
        ...filters,
        page,
        page_size: PAGE_SIZE,
      });
      setItems(data.results || []);
      setCount(data.count || 0);
      setSchemaData(data.schema || null);

    } catch (error) {
      setMessage({
        error: true,
        text:
          error.response?.status === 403
            ? "You don't have permission to manage testimonials."
            : "Unable to load testimonials.",
      });
    } finally {
      setLoading(false);
    }
  }, [filters, page]);
  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);
  const updateFilter = (name, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [name]: value }));
  };
  const choose = async (id, nextMode) => {
    try {
      const requests = [api.get(id)];
      if (nextMode === "edit") requests.push(api.designations());
      const [testimonialResponse, designationResponse] = await Promise.all(requests);
      const { data } = testimonialResponse;
      if (designationResponse) {
        const designationData = designationResponse.data;
        setDesignations(designationData.results || designationData.data || []);
      }
      setSelected(data.data || data);
      setMode(nextMode);
    } catch {
      setMessage({ error: true, text: "Unable to load testimonial details." });
    }
  };
  const openEdit = async () => {
    try {
      const { data } = await api.designations();
      setDesignations(data.results || data.data || []);
      setMode("edit");
    } catch {
      setMessage({ error: true, text: "Unable to load designation options." });
    }
  };
  const saveEdit = async (body) => {
    setSaving(true);
    try {
      await api.patch(selected.id, body);
      setMode(null);
      setMessage({ text: "Testimonial updated successfully." });
      load();
    } catch (error) {
      setMessage({
        error: true,
        text:
          error.response?.data?.rating?.[0] ||
          error.response?.data?.detail ||
          "Unable to update testimonial.",
      });
    } finally {
      setSaving(false);
    }
  };
  const formalizeReview = async (review) => {
    const { data } = await api.formalize(review);
    return data?.data?.review || data?.review || "";
  };
  const executeAction = async () => {
    setSaving(true);
    try {
      if (action.type === "delete") await api.remove(action.item.id);
      else
        await api.patch(
          action.item.id,
          action.type === "publish"
            ? { is_approved: true, is_active: true }
            : { is_active: false },
        );
      setMessage({
        text:
          action.type === "publish"
            ? "Testimonial published successfully."
            : action.type === "unpublish"
              ? "Testimonial unpublished successfully."
              : "Testimonial deactivated successfully.",
      });
      setSelected((current) =>
        current?.id === action.item.id
          ? {
            ...current,
            is_approved:
              action.type === "publish" ? true : current.is_approved,
            is_active: action.type === "publish",
          }
          : current,
      );
      setAction(null);
      load();
    } catch {
      setMessage({ error: true, text: "Unable to complete this action." });
    } finally {
      setSaving(false);
    }
  };
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const hasFilters = Object.values(filters).some(Boolean);
  const actionTitle =
    action?.type === "delete"
      ? "Deactivate"
      : action?.type === "publish"
        ? "Publish"
        : "Unpublish";
  return (
    <main className="tm-page">

     

      <style>{`
    .tm-page{box-sizing:border-box;min-height:100vh;padding:30px;background:linear-gradient(135deg,#f8faff,#f7f9fc 50%,#fbfbff);color:#172b4d;font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}.tm-header{display:flex;align-items:flex-end;justify-content:space-between;gap:20px;margin:0 0 24px}.tm-eyebrow{display:flex;align-items:center;gap:7px;margin-bottom:8px;color:#6d4df5;font-size:12px;font-weight:750;letter-spacing:.08em;text-transform:uppercase}.tm-header h1{margin:0;font-size:27px;letter-spacing:-.035em}.tm-header p{margin:7px 0 0;color:#607391;font-size:14px}.tm-count{padding:9px 13px;border:1px solid #e2e6f3;border-radius:10px;background:#fff;color:#526784;font-size:13px;font-weight:650;box-shadow:0 3px 12px rgba(36,52,85,.04)}.tm-count strong{color:#6d4df5}.tm-card{border:1px solid #e1e7f1;border-radius:15px;background:#fff;box-shadow:0 8px 25px rgba(28,47,84,.055)}.tm-alert{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:16px;padding:12px 14px;border:1px solid #cce8d4;border-radius:11px;background:#f3fcf5;color:#16733b;font-size:13px;font-weight:550}.tm-alert.error{border-color:#fecaca;background:#fff6f6;color:#bd2a2a}.tm-filter-card{display:grid;grid-template-columns:minmax(260px,2fr) repeat(3,minmax(145px,1fr)) auto;align-items:center;gap:11px;margin-bottom:18px;padding:13px}.tm-search{position:relative}.tm-search svg{position:absolute;top:50%;left:13px;color:#92a1b9;transform:translateY(-50%)}.tm-input{box-sizing:border-box;width:100%;height:43px;padding:0 12px;border:1px solid #d7deeb;border-radius:9px;background:#fff;color:#243957;font:inherit;font-size:13px;outline:none}.tm-search .tm-input{padding-left:38px}.tm-input:focus{border-color:#8268f7;box-shadow:0 0 0 3px rgba(109,77,245,.11)}.tm-clear{height:40px;padding:0 12px;border:0;border-radius:8px;background:#f1efff;color:#6548dc;font:inherit;font-size:13px;font-weight:700;cursor:pointer}.tm-table-card{overflow:hidden}.tm-table-scroll{overflow-x:auto}.tm-table{width:100%;min-width:975px;border-collapse:collapse}.tm-table th{padding:13px 16px;border-bottom:1px solid #e7ebf3;background:#f8faff;color:#70809b;text-align:left;font-size:10.5px;font-weight:750;letter-spacing:.055em;text-transform:uppercase}.tm-table td{padding:15px 16px;border-bottom:1px solid #edf0f5;color:#314763;font-size:13px;vertical-align:middle}.tm-table tr:last-child td{border-bottom:0}.tm-table tbody tr:hover{background:#fbfcff}.tm-reviewer{display:flex;align-items:center;gap:10px;min-width:170px}.tm-avatar{display:grid;place-items:center;width:34px;height:34px;flex:0 0 34px;border-radius:10px;background:linear-gradient(145deg,#f0ebff,#e2dbff);color:#6245d7;font-size:12px;font-weight:800}.tm-reviewer b,.tm-reviewer span{display:block;max-width:170px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.tm-reviewer b{color:#1b3153;font-size:13px}.tm-reviewer span{margin-top:3px;color:#8290a8;font-size:12px}.tm-stars{display:inline-flex;align-items:center;gap:1px;color:#f4a11a;white-space:nowrap}.tm-stars b{margin-left:5px;color:#536780;font-size:12px}.tm-snippet{max-width:260px;color:#536780;line-height:1.55}.tm-snippet:before{content:'“';color:#a99bff;font-size:19px;font-weight:800;vertical-align:-3px}.tm-type{text-transform:capitalize;font-weight:600;color:#637490}.tm-badge{display:inline-flex;align-items:center;gap:6px;padding:5px 9px;border-radius:99px;font-size:11px;font-weight:750}.tm-badge i{width:5px;height:5px;border-radius:50%;background:currentColor}.tm-badge.published{background:#e6faee;color:#168249}.tm-badge.pending{background:#fff5dc;color:#bd7a00}.tm-badge.unpublished{background:#eff2f7;color:#617187}.tm-muted,.tm-date{color:#73839a;font-size:12px}.tm-date{white-space:nowrap}.tm-actions{display:flex;gap:6px}.tm-icon{display:inline-grid;place-items:center;width:31px;height:31px;border:1px solid #dbe3f0;border-radius:8px;background:#fff;color:#57708f;cursor:pointer;transition:all .15s}.tm-icon:hover{border-color:#9e8bff;background:#f5f3ff;color:#6548dc}.tm-icon.publish{color:#168249}.tm-icon.publish:hover{border-color:#87dbac;background:#effcf4;color:#10813f}.tm-icon.unpublish{color:#c27d05}.tm-icon.unpublish:hover{border-color:#f5ca75;background:#fff8e8;color:#a96800}.tm-icon.danger{color:#d64b56}.tm-icon.danger:hover{border-color:#f2a8af;background:#fff4f5;color:#bd2531}.tm-empty{padding:60px 24px!important;text-align:center;color:#74829a}.tm-empty strong{display:block;margin-bottom:6px;color:#344963;font-size:14px}.tm-pagination{display:flex;align-items:center;justify-content:space-between;gap:14px;padding:13px 16px;border-top:1px solid #e9edf4;background:#fbfcfe}.tm-pagination-info{color:#73829a;font-size:12px}.tm-pagination-controls{display:flex;align-items:center;gap:8px}.tm-secondary,.tm-primary,.tm-danger{border-radius:8px;font:inherit;font-size:13px;font-weight:700;cursor:pointer}.tm-secondary{padding:8px 12px;border:1px solid #d3dcea;background:#fff;color:#49617e}.tm-secondary:disabled{cursor:not-allowed;opacity:.5}.tm-primary,.tm-danger{border:0;padding:10px 15px}.tm-primary{background:linear-gradient(135deg,#7558ef,#5d42d4);color:#fff;box-shadow:0 5px 13px rgba(91,63,208,.2)}.tm-danger{background:#d84450;color:#fff}.tm-modal-backdrop{position:fixed;z-index:1000;inset:0;display:flex;align-items:center;justify-content:center;padding:20px;background:rgba(13,26,49,.58);backdrop-filter:blur(3px)}.tm-modal{width:100%;max-width:620px;max-height:85vh;overflow:auto;border-radius:15px;background:#fff;box-shadow:0 24px 64px rgba(5,15,34,.28)}.tm-modal>header{display:flex;justify-content:space-between;align-items:flex-start;padding:18px 22px;border-bottom:1px solid #edf0f5}.tm-modal h2{margin:0;font-size:18px}.tm-modal header p{margin:4px 0 0;color:#8290a8;font-size:12px}.tm-modal-content{padding:22px}.tm-field{display:block;margin-bottom:16px;color:#3a4d67;font-size:13px;font-weight:700}.tm-field input,.tm-field textarea,.tm-field select{display:block;margin-top:7px}.tm-error{color:#c8323d;font-size:13px}.tm-modal-actions{display:flex;justify-content:flex-end;margin-top:20px}.tm-detail-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:16px;margin-bottom:22px}.tm-detail-grid span,.tm-detail article>span{display:block;margin-bottom:5px;color:#8290a8;font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase}.tm-detail-grid strong{display:block;color:#344963;font-size:13px;font-weight:650}.tm-detail article{padding:15px;border-radius:10px;background:#f8faff}.tm-detail article p{margin:0;color:#425873;line-height:1.65;white-space:pre-wrap}.tm-confirmation{margin:0;color:#556980;line-height:1.65}.tm-confirmation strong{display:block;margin-bottom:6px;color:#253a58}@media(max-width:1100px){.tm-filter-card{grid-template-columns:repeat(2,minmax(0,1fr))}}@media(max-width:650px){.tm-page{padding:18px 14px}.tm-header{align-items:flex-start;flex-direction:column;margin-bottom:18px}.tm-header h1{font-size:24px}.tm-filter-card{grid-template-columns:1fr}.tm-count{align-self:flex-start}.tm-pagination{align-items:flex-start;flex-direction:column}.tm-modal-backdrop{padding:12px}.tm-modal>header,.tm-modal-content{padding:17px}.tm-detail-grid{grid-template-columns:1fr}}
      `}</style>
      <style>{`
        .tm-table{min-width:900px}.tm-table th{padding:16px;font-size:12px}.tm-table td{padding:22px 16px;font-size:15px}.tm-table th:nth-child(2),.tm-table td:nth-child(2){min-width:135px}.tm-stars{display:inline-flex!important;flex-flow:row nowrap!important;align-items:center!important;width:max-content!important;white-space:nowrap!important}.tm-stars svg{display:block!important;flex:0 0 auto!important}.tm-stars b{display:inline!important;white-space:nowrap!important}.tm-snippet{max-width:400px;font-size:15px;line-height:1.65}.tm-type{font-size:15px}.tm-badge{padding:7px 11px;font-size:12px}.tm-icon{width:36px;height:36px}.tm-pagination-info{font-size:13px}
        .tm-contact{max-width:170px;line-height:1.4}
        .tm-company-logo{display:grid;place-items:center;width:38px;height:38px;border:1px solid #e1e7f1;border-radius:8px;background:#fff;object-fit:contain}
        .tm-logo-placeholder{color:#8290a8;font-size:12px}
        .tm-help{display:block;margin-top:6px;color:#8290a8;font-size:11px;font-weight:500}
        .tm-detail-logo{margin:-6px 0 22px}.tm-detail-logo>span{display:block;margin-bottom:5px;color:#8290a8;font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase}.tm-detail-logo img{width:90px;height:58px;border:1px solid #e1e7f1;border-radius:8px;object-fit:contain}
        .tm-modal{max-width:980px}.tm-modal>header{padding:24px 28px}.tm-modal h2{font-size:23px}.tm-modal header p{font-size:14px}.tm-modal-content{padding:28px}.tm-detail-grid{gap:22px}.tm-detail-grid span,.tm-detail article>span{font-size:12px}.tm-detail-grid strong{font-size:16px}.tm-detail article{padding:20px}.tm-detail article p{font-size:16px;line-height:1.75}.tm-detail-actions{display:flex;justify-content:flex-end;gap:10px;margin-top:22px}.tm-detail-actions button{display:inline-flex;align-items:center;gap:7px;font-size:15px}.tm-unpublish-action{color:#a96800;border-color:#f5ca75;background:#fff8e8}.tm-edit-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:0 20px}.tm-edit-full{margin-top:2px}.tm-field{font-size:15px}.tm-field input,.tm-field textarea,.tm-field select{font-size:15px}.tm-field input:disabled{background:#f6f8fc;color:#536780;cursor:not-allowed}.tm-help{font-size:12px}@media(max-width:650px){.tm-edit-grid{grid-template-columns:1fr}.tm-modal-content{padding:20px}.tm-detail-actions{align-items:stretch;flex-direction:column}.tm-detail-actions button{justify-content:center}}
        .tm-modal{max-width:980px}.tm-modal>header{padding:24px 28px}.tm-modal h2{font-size:23px}.tm-modal header p{font-size:14px}.tm-modal-content{padding:28px}.tm-detail-grid{gap:22px}.tm-detail-grid span,.tm-detail article>span{font-size:12px}.tm-detail-grid strong{font-size:16px}.tm-detail article{padding:20px}.tm-detail article p{font-size:16px;line-height:1.75}.tm-detail-actions{display:flex;justify-content:flex-end;gap:10px;margin-top:22px}.tm-detail-actions button{display:inline-flex;align-items:center;gap:7px;font-size:15px}.tm-unpublish-action{color:#a96800;border-color:#f5ca75;background:#fff8e8}.tm-edit-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:0 20px}.tm-edit-full{margin-top:2px}.tm-field{font-size:15px}.tm-field input,.tm-field textarea,.tm-field select{font-size:15px}.tm-field input:disabled{background:#f6f8fc;color:#536780;cursor:not-allowed}.tm-help{font-size:12px}.tm-formalize-panel{align-self:start;margin:0 0 16px;padding:18px;border:1px dashed #b9a9ff;border-radius:10px;background:#f8f6ff}.tm-formalize-panel span{display:block;color:#3a2b7b;font-size:15px;font-weight:750}.tm-formalize-panel p{margin:7px 0 14px;color:#64748b;font-size:13px;line-height:1.45}.tm-formalize-panel button{font-size:14px}.tm-selection-notice{margin:0 0 14px;padding:10px 13px;border:1px solid #cde9d7;border-radius:8px;background:#f1fcf4;color:#187643;font-size:14px;font-weight:650}.tm-selection-notice.original{border-color:#d9e1ee;background:#f6f8fb;color:#536780}.tm-review-input.formalized{border:2px solid #8062f5!important;background:#fbfaff!important;box-shadow:0 0 0 3px rgba(112,80,232,.11)}.tm-comparison-intro{margin:0 0 18px;color:#64748b;font-size:14px;line-height:1.5}.tm-comparison-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px}.tm-comparison-card{display:flex;flex-direction:column;min-height:260px;padding:18px;border:1px solid #dbe3f0;border-radius:12px;background:#f8faff}.tm-comparison-card.formalized{background:#faf7ff;border-color:#dccfff}.tm-comparison-card span{color:#65748d;font-size:12px;font-weight:800;letter-spacing:.05em;text-transform:uppercase}.tm-comparison-card p{flex:1;margin:12px 0 18px;color:#344963;font-size:15px;line-height:1.65;white-space:pre-wrap}.tm-comparison-card button{align-self:flex-start}@media(max-width:650px){.tm-edit-grid,.tm-comparison-grid{grid-template-columns:1fr}.tm-modal-content{padding:20px}.tm-detail-actions{align-items:stretch;flex-direction:column}.tm-detail-actions button{justify-content:center}}
      `}</style>
      <header className="tm-header">
        <div>
          {/* <div className="tm-eyebrow"><CheckCircle2 size={14} /> Content moderation</div> */}
          <h1>Testimonials</h1>
          {/* <p>Review customer feedback and control what appears on the public website.</p> */}
        </div>
        <div className="tm-count">
          <strong>{count}</strong>{" "}
          {count === 1 ? "testimonial" : "testimonials"}
        </div>
      </header>
      {message && (
        <div
          className={`tm-alert${message.error ? " error" : ""}`}
          role="alert"
        >
          <span>{message.text}</span>
          <IconButton label="Dismiss message" onClick={() => setMessage(null)}>
            <X size={16} />
          </IconButton>
        </div>
      )}
      <section
        className="tm-card tm-filter-card"
        aria-label="Filter testimonials"
      >
        <div className="tm-search">
          <Search size={16} />
          <input
            className="tm-input"
            placeholder="Search reviews, names, or email…"
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
          />
        </div>
        <select
          aria-label="Reviewer type"
          className="tm-input"
          value={filters.reviewer_type}
          onChange={(e) => updateFilter("reviewer_type", e.target.value)}
        >
          <option value="">All reviewer types</option>
          <option value="buyer">Buyer</option>
          <option value="supplier">Supplier</option>
          <option value="default">Default</option>
        </select>
        <select
          aria-label="Status"
          className="tm-input"
          value={filters.status}
          onChange={(e) => updateFilter("status", e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="published">Published</option>
          <option value="unpublished">Unpublished / inactive</option>
        </select>
        <select
          aria-label="Rating"
          className="tm-input"
          value={filters.rating}
          onChange={(e) => updateFilter("rating", e.target.value)}
        >
          <option value="">All ratings</option>
          {[1, 2, 3, 4, 5].map((rating) => (
            <option value={rating} key={rating}>
              {rating} stars
            </option>
          ))}
        </select>
        {hasFilters && (
          <button
            type="button"
            className="tm-clear"
            onClick={() => {
              setFilters({
                search: "",
                reviewer_type: "",
                status: "",
                rating: "",
              });
              setPage(1);
            }}
          >
            Clear filters
          </button>
        )}
      </section>
      <section className="tm-card tm-table-card">
        <div className="tm-table-scroll">
          <table className="tm-table">
            <thead>
              <tr>
                {[
                  "Business name",
                  "Rating",
                  "Review",
                  "Type",
                  "Status",
                  "View",
                ].map((name) => (
                  <th key={name}>{name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="tm-empty">
                    <strong>Loading testimonials…</strong>Please wait while
                    feedback is retrieved.
                  </td>
                </tr>
              ) : !items.length ? (
                <tr>
                  <td colSpan="6" className="tm-empty">
                    <strong>
                      {hasFilters
                        ? "No testimonials match these filters"
                        : "No testimonials found"}
                    </strong>
                    {hasFilters
                      ? "Try clearing or broadening your filters."
                      : "Testimonials submitted by customers will appear here."}
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id}>
                    <td><strong>{item.business_name || "—"}</strong></td>
                    <td>
                      <Stars rating={item.rating} showValue={false} />
                    </td>
                    <td>
                      <div className="tm-snippet">
                        {item.review.length > 105
                          ? `${item.review.slice(0, 105)}…`
                          : item.review}
                      </div>
                    </td>
                    <td>
                      <span className="tm-type">{item.reviewer_type}</span>
                    </td>
                    <td>
                      <StatusBadge item={item} />
                    </td>
                    <td>
                      <div className="tm-actions">
                        <IconButton
                          label="View testimonial"
                          onClick={() => choose(item.id, "view")}
                        >
                          <Eye size={15} />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {count > 0 && (
          <footer className="tm-pagination">
            <span className="tm-pagination-info">
              Showing {(page - 1) * PAGE_SIZE + 1}–
              {Math.min(page * PAGE_SIZE, count)} of {count} testimonials
            </span>
            <div className="tm-pagination-controls">
              <button
                type="button"
                className="tm-secondary"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
              <span className="tm-pagination-info">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="tm-secondary"
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          </footer>
        )}
      </section>


  {/* {schemaData && (
  <section
    className="tm-card"
    style={{
      marginTop: "20px",
      padding: "20px",
      overflowX: "auto",
    }}
  >
    <h3 style={{ marginBottom: "12px" }}>Generated Schema</h3>

    <pre
      style={{
        margin: 0,
        padding: "16px",
        background: "#f8fafc",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        fontSize: "13px",
        lineHeight: "1.5",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {JSON.stringify(schemaData, null, 2)}
    </pre>
  </section>
)} */}
      {selected && mode && (
        <Modal
          title={mode === "edit" ? "Edit testimonial" : "Testimonial details"}
          closeOnBackdrop={false}
          disableClose={formalizing}
          onClose={() => {
            if (formalizing) return;
            setSelected(null);
            setMode(null);
          }}
        >
          {mode === "edit" ? (
            <TestimonialForm
              testimonial={selected}
              designations={designations}
              onSave={saveEdit}
              onFormalize={formalizeReview}
              saving={saving}
              formalizing={formalizing}
              onFormalizingChange={setFormalizing}
            />
          ) : (
            <Detail
              item={selected}
              saving={saving}
              onEdit={openEdit}
              onPublishToggle={() =>
                setAction({ type: selected.is_active ? "unpublish" : "publish", item: selected })
              }
              onDeactivate={() => setAction({ type: "delete", item: selected })}
            />
          )}
        </Modal>
      )}
      {action && (
        <Modal
          title={`${actionTitle} testimonial`}
          onClose={() => !saving && setAction(null)}
        >
          <p className="tm-confirmation">
            <strong>
              {reviewerName(action.item.created_by)}’s testimonial
            </strong>
            {action.type === "publish"
              ? "This testimonial will become available on the public website."
              : action.type === "unpublish"
                ? "This testimonial will no longer be shown publicly."
                : "This testimonial will be removed from active use but will not be permanently deleted."}
          </p>
          <div className="tm-modal-actions" style={{ gap: 10 }}>
            <button
              type="button"
              className="tm-secondary"
              onClick={() => setAction(null)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className={action.type === "delete" ? "tm-danger" : "tm-primary"}
              onClick={executeAction}
              disabled={saving}
            >
              {saving ? "Working…" : "Confirm"}
            </button>
          </div>
        </Modal>




      )}



    </main>
  );
}
